import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  registerSellerPortalI18n,
  SELLER_PORTAL_NS,
} from "@/features/seller-portal/i18n";
import { usePhoneChangeSend, usePhoneChangeVerify } from "@/features/seller-portal/hooks";

// Mirrors the backend's MyPhoneChangeSend._UPDATE_E164_PATTERN
// (garden/features/marketplace/schemas.py) — catches a blank/malformed
// number before the round-trip instead of leaving the seller staring at a
// disabled button (SCRUM-173: the send endpoint alone can take 2-3s).
const E164_RE = /^\+[1-9]\d{7,14}$/;

export function PhoneChangeDialog({ onClose }: { onClose: () => void }): ReactElement {
  // Module-level registration in seller-portal/i18n.ts runs at import time,
  // before i18next may have finished `init()` (it no-ops if so). Register
  // again on render — a plain call, not a hook, so it's cheap and safe on
  // every render — so this dialog resolves its copy even when mounted
  // before any sibling section (which currently register it as a side
  // effect of their own SettingsSectionCard wrapper) has rendered first.
  registerSellerPortalI18n();
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const send = usePhoneChangeSend();
  const verify = usePhoneChangeVerify();
  const [phase, setPhase] = useState<"enter" | "verify">("enter");
  const [newPhone, setNewPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  const onSend = (): void => {
    if (!E164_RE.test(newPhone.trim())) {
      setPhoneError(t("phoneDialog.phoneInvalid"));
      return;
    }
    setPhoneError(null);
    send.mutate(
      { new_phone_e164: newPhone.trim() },
      {
        onSuccess: () => {
          toast.success(t("phoneDialog.sent"));
          setPhase("verify");
        },
        // SCRUM-173: distinct from the verify-step message below — this
        // fires for a phone the server rejected (format/already-taken),
        // never for a wrong code, so it must not say "code".
        onError: () => toast.error(t("phoneDialog.sendError")),
      },
    );
  };

  const onVerify = (): void => {
    if (otp.trim().length === 0) {
      setOtpError(t("phoneDialog.codeRequired"));
      return;
    }
    setOtpError(null);
    verify.mutate(
      { otp: otp.trim() },
      {
        onSuccess: () => {
          toast.success(t("settings.saved"));
          onClose();
        },
        onError: () => toast.error(t("phoneDialog.error")),
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="phone-change-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm rounded-[var(--g-radius-lg)] bg-[var(--g-surface)] p-5">
        <h3 className="mb-3 text-sm font-semibold text-[var(--g-ink)]">
          {t("phoneDialog.title")}
        </h3>

        {phase === "enter" ? (
          <div className="flex flex-col gap-3">
            <Label htmlFor="pc-new">{t("phoneDialog.newPhone")}</Label>
            <Input
              id="pc-new"
              data-testid="pc-new-phone"
              value={newPhone}
              disabled={send.isPending}
              aria-invalid={phoneError !== null || undefined}
              aria-describedby={phoneError !== null ? "pc-new-error" : undefined}
              onChange={(e) => {
                setNewPhone(e.target.value);
                if (phoneError !== null) setPhoneError(null);
              }}
              placeholder={t("phoneDialog.phonePlaceholder")}
            />
            {phoneError !== null ? (
              <p
                id="pc-new-error"
                role="alert"
                data-testid="pc-new-error"
                className="text-xs text-[var(--g-error)]"
              >
                {phoneError}
              </p>
            ) : null}
            <button
              type="button"
              data-testid="pc-send"
              disabled={send.isPending}
              onClick={onSend}
              className="inline-flex items-center justify-center gap-2 rounded-[var(--g-radius)] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {send.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {t("phoneDialog.sending")}
                </>
              ) : (
                t("phoneDialog.send")
              )}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Label htmlFor="pc-otp">{t("phoneDialog.code")}</Label>
            <Input
              id="pc-otp"
              data-testid="pc-otp"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              disabled={verify.isPending}
              aria-invalid={otpError !== null || undefined}
              aria-describedby={otpError !== null ? "pc-otp-error" : undefined}
              onChange={(e) => {
                setOtp(e.target.value);
                if (otpError !== null) setOtpError(null);
              }}
            />
            {otpError !== null ? (
              <p
                id="pc-otp-error"
                role="alert"
                data-testid="pc-otp-error"
                className="text-xs text-[var(--g-error)]"
              >
                {otpError}
              </p>
            ) : null}
            <button
              type="button"
              data-testid="pc-verify"
              disabled={verify.isPending}
              onClick={onVerify}
              className="inline-flex items-center justify-center gap-2 rounded-[var(--g-radius)] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {verify.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {t("phoneDialog.verifying")}
                </>
              ) : (
                t("phoneDialog.verify")
              )}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-3 text-sm text-[var(--g-ink-3)]"
        >
          {t("nav.close")}
        </button>
      </div>
    </div>
  );
}
